import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { FaArrowRight, FaLock, FaEnvelope, FaGem } from "react-icons/fa";
import { IoAlertCircleSharp } from "react-icons/io5";
import useAuth from "../useAuth";
import Loading from "../Components/global/Loading";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";

export default function LoginPage() {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const { user, authMessage, loading, loginWithEmailAndPassword } = useAuth();

  const handleLogin = async (data) => {
    loginWithEmailAndPassword(data?.email, data?.password);
  };

  if (user) {
    return <Navigate to="/my/carouselManager" />;
  }

  return (
    <div className="min-h-screen bg-[#0D0C0B] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Dynamic Background Glows */}
      <div
        className="absolute top-10 left-1/4 w-96 h-96 rounded-full pointer-events-none opacity-20"
        style={{
          background: "radial-gradient(circle, #C8A165 0%, transparent 70%)",
          filter: "blur(60px)",
        }}
      />
      <div
        className="absolute bottom-10 right-1/4 w-96 h-96 rounded-full pointer-events-none opacity-15"
        style={{
          background: "radial-gradient(circle, #9E7A48 0%, transparent 70%)",
          filter: "blur(60px)",
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-4xl bg-[#141210]/90 backdrop-blur-xl border border-[#C8A165]/20 rounded-3xl shadow-2xl overflow-hidden grid grid-cols-1 md:grid-cols-12 relative z-10"
      >
        {/* Left Side: Brand Panel */}
        <div className="md:col-span-5 bg-gradient-to-br from-[#1A1815] to-[#0A0806] p-8 md:p-12 flex flex-col justify-between border-b md:border-b-0 md:border-r border-[#C8A165]/15 relative overflow-hidden">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-[#C8A165] to-[#F5E5C9] flex items-center justify-center text-[#121110] font-bold shadow-md shadow-[#C8A165]/20">
              <FaGem size={20} />
            </div>
            <div>
              <h2 className="font-cormorant font-bold text-xl text-[#F8F5F2] tracking-wide">
                Tanushree <span className="text-[#C8A165] italic">Saha</span>
              </h2>
              <p className="font-poppins text-[0.62rem] text-[#C8A165]/70 tracking-widest uppercase">
                Bridal Studio Admin
              </p>
            </div>
          </div>

          <div className="my-12">
            <h3 className="font-cormorant text-3xl font-semibold text-cream leading-tight mb-3">
              Manage Your <br />
              <em className="gold-text italic">Couture Artistry</em>
            </h3>
            <p className="text-xs text-cream/60 leading-relaxed font-poppins">
              Welcome back to your centralized studio console. Access booking requests, portfolio galleries, pricing plans, and client management.
            </p>
          </div>

          <div className="text-[0.65rem] text-cream/40 font-poppins uppercase tracking-widest">
            © 2026 Tanushree Saha • All Rights Reserved
          </div>
        </div>

        {/* Right Side: Form Panel */}
        <div className="md:col-span-7 p-8 md:p-12 flex flex-col justify-center bg-[#141210]">
          <div className="mb-8">
            <span className="text-[0.65rem] font-poppins font-semibold uppercase tracking-[0.25em] text-[#C8A165]">
              Secure Access
            </span>
            <h2 className="text-2xl font-bold text-cream font-cormorant mt-1">
              Sign In to Your Account
            </h2>
          </div>

          <form onSubmit={handleSubmit(handleLogin)} className="space-y-5">
            {/* Email Field */}
            <div>
              <label className="block text-xs font-medium text-cream/80 mb-1.5 font-poppins">
                Email Address
              </label>
              <div className="relative">
                <FaEnvelope className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#C8A165]/60 text-sm" />
                <input
                  type="email"
                  placeholder="admin@tanushreesaha.com"
                  {...register("email", {
                    required: "Please enter your email address",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Please enter a valid email address",
                    },
                  })}
                  className="w-full text-xs bg-[#0D0C0B] text-cream pl-10 pr-4 py-3 rounded-xl border border-[#C8A165]/20 focus:outline-none focus:border-[#C8A165] transition-colors font-poppins"
                />
              </div>
              {errors?.email && (
                <p className="text-red-400 text-[0.7rem] mt-1 font-poppins">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-xs font-medium text-cream/80 mb-1.5 font-poppins">
                Password
              </label>
              <div className="relative">
                <FaLock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#C8A165]/60 text-sm" />
                <input
                  type="password"
                  placeholder="••••••••••••"
                  {...register("password", {
                    required: "Please enter your password",
                    minLength: {
                      value: 3,
                      message: "Password must be at least 3 characters",
                    },
                  })}
                  className="w-full text-xs bg-[#0D0C0B] text-cream pl-10 pr-4 py-3 rounded-xl border border-[#C8A165]/20 focus:outline-none focus:border-[#C8A165] transition-colors font-poppins"
                />
              </div>
              {errors?.password && (
                <p className="text-red-400 text-[0.7rem] mt-1 font-poppins">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Auth error message */}
            {authMessage && (
              <div className="flex items-center gap-2 text-red-400 text-xs bg-red-950/40 border border-red-500/20 p-3 rounded-xl font-poppins">
                <IoAlertCircleSharp className="text-base flex-shrink-0" />
                <span>{authMessage?.slice(5).split("-").join(" ")}</span>
              </div>
            )}

            {/* Submit Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="w-full flex items-center justify-center gap-2 text-[#121110] font-bold text-xs uppercase tracking-widest py-3.5 px-6 rounded-xl font-poppins shadow-lg shadow-[#C8A165]/20 transition-all mt-4"
              style={{
                background:
                  "linear-gradient(135deg, #C8A165 0%, #F5E5C9 50%, #9E7A48 100%)",
              }}
            >
              Sign In to Portal <FaArrowRight size={12} />
            </motion.button>

            {/* Register Option */}
            <div className="pt-4 text-center">
              <span className="text-xs text-cream/50 font-poppins">
                Don't have an account?{" "}
              </span>
              <button
                type="button"
                onClick={() => navigate("/register")}
                className="text-xs font-semibold text-[#C8A165] hover:underline font-poppins ml-1"
              >
                Register Here
              </button>
            </div>
          </form>
        </div>
      </motion.div>

      {loading && <Loading />}
    </div>
  );
}
